import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import {
  Card, Form, Input, Button, Select, Upload, InputNumber,
  message, Space
} from 'antd';
import {
  ArrowLeftOutlined, UploadOutlined
} from '@ant-design/icons';
import { CREATE_VOCAL, GET_PRESIGNED_URL_FOR_VOCAL } from '../../graphql/mutation';
import { uploadToR2 } from '../../utils/uploadToR2';

const { Option } = Select;

const CreateVocal = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [uploading, setUploading] = useState(false);
  const [vocalFileUrl, setVocalFileUrl] = useState(null);
  const [fileList, setFileList] = useState([]);

  const [getPresignedUrl] = useMutation(GET_PRESIGNED_URL_FOR_VOCAL);
  const [createVocal, { loading }] = useMutation(CREATE_VOCAL, {
    onCompleted: (data) => {
      if (data.createVocal.success) {
        message.success(data.createVocal.message || 'Vocal created successfully');
        navigate('/vocals');
      } else {
        message.error(data.createVocal.message || 'Failed to create vocal');
      }
    },
    onError: (error) => {
      message.error(`Failed to create vocal: ${error.message}`);
    }
  });

  const handleBeforeUpload = (file) => {
    const isAudio = file.type.startsWith('audio/');
    if (!isAudio) {
      message.error('You can only upload audio files!');
      return Upload.LIST_IGNORE;
    }
    const isLt50M = file.size / 1024 / 1024 < 50;
    if (!isLt50M) {
      message.error('Audio file must be smaller than 50MB!');
      return Upload.LIST_IGNORE;
    }
    return false; // Prevent auto upload
  };

  const handleCustomUpload = async ({ file, onSuccess, onError }) => {
    setUploading(true);
    try {
      const { data } = await getPresignedUrl({
        variables: {
          filename: file.name,
          contentType: file.type
        }
      });

      if (!data.getPresignedUrlForVocal.success) {
        throw new Error(data.getPresignedUrlForVocal.message || 'Failed to get upload URL');
      }

      const { uploadUrl, fileUrl } = data.getPresignedUrlForVocal;
      const uploadResult = await uploadToR2(file, uploadUrl, file.type);

      if (!uploadResult.success) {
        throw new Error(uploadResult.message || 'Failed to upload file');
      }

      setVocalFileUrl(fileUrl);
      setFileList([file]);
      message.success('Vocal file uploaded successfully');
      onSuccess(uploadResult, file);
    } catch (error) {
      message.error(error.message || 'Upload failed');
      onError(error);
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setVocalFileUrl(null);
    setFileList([]);
  };

  const onFinish = async (values) => {
    if (!vocalFileUrl) {
      message.error('Please upload a vocal file first');
      return;
    }

    const input = {
      vocalUrl: vocalFileUrl,
      vocalGender: values.vocalGender,
      title: values.title || undefined,
      duration: values.duration || undefined,
    };

    createVocal({ variables: { input } });
  };

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/vocals')}
            >
              Back
            </Button>
            <span>Create Vocal</span>
          </Space>
        }
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            vocalGender: 'male'
          }}
          style={{ maxWidth: 600 }}
        >
          {/* Upload Vocal File */}
          <Form.Item
            label="Vocal File"
            required
            tooltip="Upload your vocal MP3 file (max 50MB)"
          >
            <Upload
              beforeUpload={handleBeforeUpload}
              customRequest={handleCustomUpload}
              onRemove={handleRemove}
              fileList={fileList}
              maxCount={1}
            >
              <Button icon={<UploadOutlined />} loading={uploading}>
                {uploading ? 'Uploading...' : 'Select Vocal File'}
              </Button>
            </Upload>
            {vocalFileUrl && (
              <div style={{ marginTop: 8, color: '#52c41a' }}>
                ✓ File uploaded successfully
              </div>
            )}
          </Form.Item>

          {/* Title */}
          <Form.Item
            name="title"
            label="Title"
            tooltip="Optional title for the vocal"
          >
            <Input placeholder="e.g., My Vocal Recording" maxLength={100} />
          </Form.Item>

          {/* Vocal Gender */}
          <Form.Item
            name="vocalGender"
            label="Vocal Gender"
            rules={[{ required: true, message: 'Please select vocal gender' }]}
          >
            <Select>
              <Option value="male">Male</Option>
              <Option value="female">Female</Option>
            </Select>
          </Form.Item>

          {/* Duration */}
          <Form.Item
            name="duration"
            label="Duration (seconds)"
            tooltip="Approximate duration of the vocal in seconds"
          >
            <InputNumber min={1} max={600} style={{ width: '100%' }} placeholder="e.g., 120" />
          </Form.Item>

          {/* Submit Button */}
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              disabled={!vocalFileUrl || uploading}
              size="large"
              block
            >
              Create Vocal
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default CreateVocal;
